import { useState, FormEvent } from 'react';

interface ContactModalProps {
  onClose: () => void;
}

export default function ContactModal({ onClose }: ContactModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5l5es5porqtj2nscjbg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-midnight-navy/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
        <div className="p-6 border-b border-soft-silver flex items-center justify-between">
          <h3 className="text-2xl font-serif font-bold text-midnight-navy">Contact Our Team</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-gray hover:text-midnight-navy transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-readdy-form id="contact-form">
          <div>
            <label className="block text-sm font-medium text-midnight-navy mb-2">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              required
              className="w-full px-4 py-3 border border-soft-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-wealth-gold text-sm text-midnight-navy"
              placeholder="John Smith"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-midnight-navy mb-2">
              Email Address *
            </label>
            <input
              type="email"
              name="email"
              required
              className="w-full px-4 py-3 border border-soft-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-wealth-gold text-sm text-midnight-navy"
              placeholder="john@company.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-midnight-navy mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              className="w-full px-4 py-3 border border-soft-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-wealth-gold text-sm text-midnight-navy"
              placeholder="+1 (555) 000-0000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-midnight-navy mb-2">
              Message *
            </label>
            <textarea
              name="message"
              rows={5}
              maxLength={500}
              required
              className="w-full px-4 py-3 border border-soft-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-wealth-gold text-sm text-midnight-navy resize-none"
              placeholder="How can we help you?"
            ></textarea>
            <p className="text-xs text-slate-gray mt-1">Maximum 500 characters</p>
          </div>

          {submitStatus === 'success' && (
            <div className="p-4 bg-emerald-insight/10 border border-emerald-insight rounded-lg">
              <p className="text-sm text-emerald-insight font-medium">
                Message sent! We'll respond within 24 hours.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 font-medium">
                Something went wrong. Please try again.
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-6 py-4 bg-wealth-gold text-midnight-navy text-base font-semibold rounded-lg hover:bg-wealth-gold/90 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </div>
    </div>
  );
}
